import React, { useEffect, useMemo, useState } from 'react'
import { Hint } from '@tecsinapse/cortex-react'
import { OptionSelect } from '@/core/utils/types'
import { Select } from '@/core/components/select'
import { isArrayofStrings } from '@/core/utils/is-array-of-strings'
import { ErrorMessage } from '@hookform/error-message'
import { useController, useFormContext } from 'react-hook-form'

interface FormSingleSelectProps {
  name: string
  customValue?: { label: string; value: boolean }
  options: OptionSelect[] | { label: string; value: boolean }[]
  customOnChange?: (e: OptionSelect) => void
  label?: string
  customOnBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void
  disabled?: boolean
  withSearch?: boolean
}

export const FormSingleSelect = ({
  name,
  customValue,
  options: _options = [],
  customOnChange,
  label,
  customOnBlur,
  disabled,
  withSearch = false
}: FormSingleSelectProps) => {
  const { setValue, control } = useFormContext()
  const { field, formState, fieldState } = useController({ name, control })
  const errors = formState?.errors ?? {}
  const error = fieldState?.error ?? []
  const submitCount = formState?.submitCount
  const value = customValue ?? field.value
  const [searchText, setSearchText] = useState('')

  const labelExtractor = (option: OptionSelect) => option.label
  const keyExtractor = (option: OptionSelect) => option.value

  const filteredOptions = (_options ?? []).filter(
    (option: OptionSelect | { label: string; value: boolean }) =>
      labelExtractor(option).toLowerCase().includes(searchText.toLowerCase())
  )

  const formatValue =
    typeof value === 'string'
      ? _options.find(
          (op: OptionSelect | { label: string; value: boolean }) =>
            op.value === value
        )
      : _options.find(
          (op: OptionSelect | { label: string; value: boolean }) =>
            op.value === value?.value
        )

  useEffect(() => {
    if (isArrayofStrings(value))
      setValue(name, formatValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      })
  }, [])

  const options = useMemo(
    () => (withSearch ? filteredOptions : _options),
    [filteredOptions, _options, withSearch]
  )
  return (
    <div
      className={'w-full'}
      onBlur={e => {
        if (customOnBlur) {
          customOnBlur(e)
        }
      }}
    >
      <Select
        disabled={disabled}
        withSearch={withSearch}
        label={label}
        setInputSearch={setSearchText}
        options={options}
        keyExtractor={keyExtractor}
        labelExtractor={labelExtractor}
        value={formatValue}
        onSelect={(selectedValue: OptionSelect) => {
          setValue(name, selectedValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true
          })
          if (customOnChange) {
            customOnChange(selectedValue)
          }
        }}
        onBlur={field.onBlur}
        onChange={field.onChange}
        name={name}
      />
      {submitCount > 0 && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Hint variants={{ intent: 'error' }}>{message}</Hint>
          )}
        />
      ) : null}
    </div>
  )
}

export default FormSingleSelect
